import { nextTick, onBeforeUnmount, watch, type Ref } from 'vue'

// Fixed body also prevents background scrolling in mobile Safari.
export function useModal(open: Ref<boolean>, close: () => void) {
  let restore: (() => void) | undefined
  watch(open, async (visible) => {
    if (!visible) {
      restore?.()
      restore = undefined
      return
    }
    const body = document.body
    const previousStyle = body.getAttribute('style')
    const scrollX = window.scrollX
    const scrollY = window.scrollY
    const previousFocus = document.activeElement as HTMLElement | null
    Object.assign(body.style, {
      position: 'fixed', top: `-${scrollY}px`, left: `-${scrollX}px`, width: '100%', overflow: 'hidden',
    })
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
      if (event.key !== 'Tab') return
      const dialog = document.querySelector<HTMLElement>('[aria-modal="true"]')
      const controls = [...dialog?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), video[controls], iframe') ?? []]
        .filter((element) => element.tabIndex !== -1 && !element.closest('[inert], [hidden], [aria-hidden="true"]'))
      if (!controls?.length) return
      const first = controls[0]!
      const last = controls[controls.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    restore = () => {
      document.removeEventListener('keydown', onKey)
      if (previousStyle === null) body.removeAttribute('style')
      else body.setAttribute('style', previousStyle)
      window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' })
      previousFocus?.focus({ preventScroll: true })
    }
    await nextTick()
    if (open.value) document.querySelector<HTMLElement>('[aria-modal="true"] button')?.focus({ preventScroll: true })
  }, { flush: 'post' })
  onBeforeUnmount(() => restore?.())
}
